<template>
  <layout>
    <div class="zone-wrapper">
      <!--   map will be injected here   -->
      <div id="map-holder" class="h-100"></div>
      <manager-panel
        :show="zoneManagerPanelVisibility"
        :tab-index="tab"
        @on-country-zone-row-click="onCountryZoneRowClick"
        @on-in-country-zone-row-click="onCountryZoneRowClick"
        @on-tab-changed="onTabChanged"
      ></manager-panel>

      <transition name="zone-nominatim-banner-fade">
        <div
          v-if="pendingNominatimZoneTitle"
          class="zone-nominatim-quick-save"
          role="region"
          :aria-label="$t('zones.save_area_as_zone')"
        >
          <div class="zone-nominatim-quick-save__bar">
            <span class="zone-nominatim-quick-save__text">{{
              $t('zones.save_area_as_zone')
            }}</span>
            <b-button variant="light" size="sm" @click="saveNominatimQuick">
              {{ $t('zones.save') }}
            </b-button>
            <div class="zone-nominatim-quick-save__actions">
              <b-button
                variant="secondary"
                size="sm"
                class="zone-nominatim-quick-save__dismiss"
                :title="$t('general.cancel')"
                @click="dismissNominatimQuickSave"
              >
                {{ $t('general.cancel') }}
              </b-button>

            </div>
          </div>
        </div>
      </transition>
    </div>
  </layout>
</template>

<script>
/* global google */
import ManagerPanel from './zone-manager-panel'
import EventBus from '../event-bus'
import Swal from 'sweetalert2'
import Mixins from '../mixins'

import { mapActions, mapGetters } from 'vuex'
import { AdminApi } from 'stylemix-vue-admin'
import { loadGoogleMaps } from 'stylemix-base'
import { divisionPopup } from './popups'

const TAB_COUNTRY_ZONE = 0
const TAB_IN_COUNTRY_ZONE = 1

export default {
  name: 'ZoneEditorView',

  components: {
    ManagerPanel,
  },

  mixins: [Mixins],

  data() {
    return {
      map: null,
      tab: process.env.VUE_APP_MULTI_DIVISION === 'true' ? 0 : 1,
      /** After ?query= deep link, select first table row once per tab / query change */
      deepLinkAutoSelectDone: false,
      /** Nominatim search string used for the current blue preview; enables quick-save bar */
      pendingNominatimZoneTitle: null,
    }
  },

  computed: {
    ...mapGetters('zones', [
      'zoneManagerPanelVisibility',
      'activeCountryZone',
      'activeInCountryZone',
      'countryZones',
      'inCountryZones',
    ]),
  },

  watch: {
    activeCountryZone: {
      immediate: true,
      handler(zone) {
        if (!zone?.id || this.tab !== TAB_IN_COUNTRY_ZONE) return
        this.fetchInCountryZones(zone.id).catch(err =>
          this.$toast.error(err.data.message),
        )
      },
    },

    '$route.fullPath'() {
      if (this.$route.name !== 'zones') return
      this.syncZonesDataFromRoute()
    },

    countryZones: 'tryDeepLinkSelectFirstRow',

    inCountryZones: 'tryDeepLinkSelectFirstRow',

    tab() {
      if (this.$route.query.query) {
        this.deepLinkAutoSelectDone = false
      }
      this.tryDeepLinkSelectFirstRow()
    },
  },

  created() {
    this.applyZoneQueryFromRoute()
    EventBus.$on('on-zone-removed', this.clearMapDataLayer)
  },

  beforeDestroy() {
    EventBus.$off('on-zone-removed', this.clearMapDataLayer)
  },

  mounted() {
    this.syncZonesDataFromRoute()
    loadGoogleMaps(['places']).then(() => this.renderMap())
  },

  activated() {
    if (this.$route.name === 'zones') {
      this.syncZonesDataFromRoute()
    }
  },

  methods: {
    ...mapActions('zones', [
      'addCountryZone',
      'addInCountryZone',
      'updateCountryZones',
      'updateInCountryZones',
      'toggleZoneManagerPanelVisibility',
      'setActiveCountryZone',
      'unsetActiveCountryZone',
      'setActiveInCountryZone',
      'unsetActiveInCountryZone',
      'fetchCountryZones',
      'fetchInCountryZones',
    ]),

    applyZoneQueryFromRoute() {
      const q = this.$route.query.query
      this.$store.commit(
        'zones/setZoneNameSearch',
        q != null && q !== '' ? String(q) : '',
      )
    },

    async syncZonesDataFromRoute() {
      this.applyZoneQueryFromRoute()
      this.deepLinkAutoSelectDone = false
      const multiDivision = process.env.VUE_APP_MULTI_DIVISION === 'true'

      if (!multiDivision) {
        await this.fetchCountryZones()
        return
      }

      const divisionIdRaw = this.$route.query.division_id
      const divisionId =
        divisionIdRaw != null && divisionIdRaw !== ''
          ? Number(divisionIdRaw)
          : NaN
      const hasDivisionDeepLink = !Number.isNaN(divisionId)

      // Airport link: /zones?division_id=…&query=zoneName — `query` filters in-country zones, not divisions.
      // Load all divisions first, then activate the target division and open the fixed-price tab.
      if (hasDivisionDeepLink) {
        const savedSearch = this.$store.state.zones.zoneNameSearch
        this.$store.commit('zones/setZoneNameSearch', '')
        await this.fetchCountryZones()
        this.$store.commit('zones/setZoneNameSearch', savedSearch)

        const dz = this.countryZones.find(
          z => Number(z.id) === Number(divisionId),
        )
        if (dz) {
          this.setActiveCountryZone(dz)
          this.$store.commit('zones/setCurrentDivision', {
            id: dz.id,
            title: dz.title,
          })
          // Avoid auto-select running on stale zones from another division (would set deepLinkAutoSelectDone too early).
          // this.$store.commit('zones/setInCountryZones', [])
          await this.$nextTick()
          this.onTabChanged(TAB_IN_COUNTRY_ZONE)
          return
        }
      }

      if (this.tab === TAB_COUNTRY_ZONE) {
        await this.fetchCountryZones()
      } else if (this.activeCountryZone?.id) {
        await this.fetchInCountryZones(this.activeCountryZone.id)
      }
    },

    renderMap(retryCount = 0) {
      if (typeof window.google === 'undefined') {
        if (retryCount < 20) {
          setTimeout(() => this.renderMap(retryCount + 1), 100)
        }
        return
      }
      const map = new window.google.maps.Map(document.getElementById('map-holder'), {
        center: { lat: 50.8503, lng: 4.3517 },
        zoom: 8,
      })

      this.applyMapDataLayerStyle(map)

      this.map = map

      // Built-in Data layer UI does not reliably participate in map.controls ordering; use
      // setControls(null) and custom buttons with setDrawingMode (see buildZoneMapTopToolbar).
      map.data.setControls(null)
      const topToolbar = this.buildZoneMapTopToolbar(map)
      map.controls[google.maps.ControlPosition.TOP_LEFT].push(topToolbar.toolbar)

      this.initAutoComplete(map, topToolbar.pacInput)

      this.addTabsControlButtonToMap()
      this.addSaveButton()
      this.addClearButton()

      this.tryDeepLinkSelectFirstRow()
    },

    /**
     * Pick the row that best matches ?query= (full airport name), not necessarily items[0] (API order).
     */
    resolveDeepLinkZone(list, queryRaw) {
      if (!list?.length) return null
      const q = String(queryRaw).trim()
      if (!q) return list[0]

      const qLower = q.toLowerCase()
      const norm = s => (s || '').replace(/\s+/g, ' ').trim()
      // URL / DB may differ on en dash vs hyphen (e.g. " - " vs " – ").
      const normKey = s =>
        norm(String(s))
          .replace(/[\u2013\u2014]/g, '-')
          .toLowerCase()

      let found = list.find(z => z.title === q)
      if (found) return found
      found = list.find(z => z.title && z.title.toLowerCase() === qLower)
      if (found) return found
      const qNorm = norm(q)
      found = list.find(z => z.title && norm(z.title) === qNorm)
      if (found) return found
      const qKey = normKey(q)
      found = list.find(z => z.title && normKey(z.title) === qKey)
      if (found) return found
      found = list.find(
        z => z.title && z.title.toLowerCase().includes(qLower),
      )
      if (found) return found
      found = list.find(z => z.title && normKey(z.title).includes(qKey))
      if (found) return found
      // Do not fall back to list[0]: wrong row (e.g. Brussels) blocks the real match after tab switch.
      return null
    },

    /**
     * When opening /zones?query=…, mirror clicking the matching table row (see resolveDeepLinkZone).
     */
    tryDeepLinkSelectFirstRow() {
      const q = this.$route.query.query
      if (q == null || q === '' || this.deepLinkAutoSelectDone) return
      if (!this.map) return

      const multiDivision = process.env.VUE_APP_MULTI_DIVISION === 'true'
      const divRaw = this.$route.query.division_id
      const routeDivisionId =
        divRaw != null && divRaw !== '' ? Number(divRaw) : NaN

      // Links from airports use ?division_id=&query= for in-country zones. While tab is still 0,
      // countryZones are divisions — resolveDeepLinkZone would pick list[0] (often Brussels) and
      // set deepLinkAutoSelectDone, so Eindhoven never gets on-row-click after sync switches tab.
      if (multiDivision && !Number.isNaN(routeDivisionId)) {
        if (this.tab !== TAB_IN_COUNTRY_ZONE) return
        if (Number(this.activeCountryZone?.id) !== routeDivisionId) return
      }

      const list =
        this.tab === TAB_COUNTRY_ZONE
          ? this.countryZones
          : this.inCountryZones

      if (!list?.length) return

      const zone = this.resolveDeepLinkZone(list, q)
      if (!zone) return

      this.$nextTick(() => {
        this.onCountryZoneRowClick(zone)
        this.deepLinkAutoSelectDone = true
      })
    },

    /** Preview from search: OSM boundary (Nominatim) or Google viewport fallback. */
    applyMapDataLayerStyle(map) {
      map.data.setStyle(feature => {
        if (feature.getProperty('__placeSearchPreview')) {
          return {
            clickable: true,
            editable: true,
            draggable: true,
            strokeWeight: 2,
            fillColor: '#1a73e8',
            fillOpacity: 0.22,
            strokeColor: '#1a73e8',
          }
        }
        return {
          clickable: true,
          editable: true,
          draggable: true,
          strokeWeight: 1,
          fillColor: 'red',
        }
      })
    },

    clearPlaceSearchPreviewFeatures(map) {
      this.pendingNominatimZoneTitle = null
      if (!map) return
      const remove = []
      map.data.forEach(f => {
        if (f.getProperty('__placeSearchPreview')) remove.push(f)
      })
      remove.forEach(f => map.data.remove(f))
    },

    dismissNominatimQuickSave() {
      this.pendingNominatimZoneTitle = null
    },

    saveNominatimQuick() {
      if (!this.map || !this.pendingNominatimZoneTitle) return
      if (
        this.tab === TAB_IN_COUNTRY_ZONE &&
        !this.activeCountryZone?.id
      ) {
        this.$toast.error(this.$t('zones.quick_save_need_active_division'))
        return
      }
      const title = this.pendingNominatimZoneTitle.trim()
      if (!title) return

      this.map.data.toGeoJson(async geoJson => {
        if (!(geoJson.features && geoJson.features.length)) return

        geoJson.features.forEach(f => {
          if (f.properties && f.properties.__placeSearchPreview)
            delete f.properties.__placeSearchPreview
        })

        await this.saveZone(geoJson, { presetTitle: title })
        this.pendingNominatimZoneTitle = null
      })
    },

    viewportToPreviewFeature(viewport, name) {
      const ne = viewport.getNorthEast()
      const sw = viewport.getSouthWest()
      const nw = new google.maps.LatLng(ne.lat(), sw.lng())
      const se = new google.maps.LatLng(sw.lat(), ne.lng())
      return {
        type: 'Feature',
        properties: {
          __placeSearchPreview: true,
          title: name || '',
        },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [ne.lng(), ne.lat()],
              [nw.lng(), nw.lat()],
              [sw.lng(), sw.lat()],
              [se.lng(), se.lat()],
              [ne.lng(), ne.lat()],
            ],
          ],
        },
      }
    },

    /** When Places only returns a point, approximate a small rectangle (km) around it (fallback). */
    approxBoundsAroundPoint(location, km) {
      const lat = location.lat()
      const lng = location.lng()
      const dLat = km / 111
      const dLng = km / (111 * Math.cos((lat * Math.PI) / 180))
      return new google.maps.LatLngBounds(
        new google.maps.LatLng(lat - dLat, lng - dLng),
        new google.maps.LatLng(lat + dLat, lng + dLng),
      )
    },

    /** Prefer Places `name` (often English) over `formatted_address` for Nominatim matching. */
    buildNominatimSearchQuery(detail, fallbackPlace) {
      const d = detail || fallbackPlace
      if (!d) return ''
      return String(
        d.name || d.formatted_address || fallbackPlace.name || '',
      ).trim()
    },

    pickNominatimPolygonResult(results) {
      if (!Array.isArray(results)) return null
      return (
        results.find(
          r =>
            r.geojson &&
            (r.geojson.type === 'Polygon' ||
              r.geojson.type === 'MultiPolygon'),
        ) || null
      )
    },

    applyNominatimGeoJsonToMap(map, geojson, label) {
      const feature = {
        type: 'Feature',
        properties: {
          __placeSearchPreview: true,
          title: label || '',
        },
        geometry: geojson,
      }
      map.data.addGeoJson(feature)
      this.fitMapToPlaceSearchPreview(map)
    },

    fitMapToPlaceSearchPreview(map) {
      const bounds = new google.maps.LatLngBounds()
      let has = false
      map.data.forEach(feature => {
        if (!feature.getProperty('__placeSearchPreview')) return
        has = true
        const g = feature.getGeometry()
        if (g && g.forEachLatLng) {
          g.forEachLatLng(latLng => bounds.extend(latLng))
        }
      })
      if (has && !bounds.isEmpty()) map.fitBounds(bounds)
    },

    /**
     * Fallback if Nominatim has no polygon: Google viewport rectangle (or box around point).
     */
    finishWithViewportFallback(map, geometry, label, typesHint, placeRef) {
      this.pendingNominatimZoneTitle = null
      let viewport = geometry.viewport
      if (!viewport && geometry.location) {
        const types = typesHint || placeRef.types || []
        const km = types.includes('airport') ? 4 : 3
        viewport = this.approxBoundsAroundPoint(geometry.location, km)
      }
      if (!viewport) return

      const fc = this.viewportToPreviewFeature(viewport, label || placeRef.name)
      map.data.addGeoJson(fc)
      map.fitBounds(viewport)
    },

    async fetchNominatimBoundaryOrFallback(
      map,
      detail,
      fallbackPlace,
    ) {
      const placeRef = detail || fallbackPlace
      const geometry = detail.geometry || fallbackPlace.geometry
      const label = detail.name || fallbackPlace.name
      const typesHint = detail.types || fallbackPlace.types

      const q = this.buildNominatimSearchQuery(detail, fallbackPlace)
      if (q) {
        try {
          const api = new AdminApi('geocoding/nominatim')
          api.setParameter('q', q)
          const results = await api.all()
          const hit = this.pickNominatimPolygonResult(results)
          if (hit && hit.geojson) {
            this.applyNominatimGeoJsonToMap(map, hit.geojson, label)
            this.pendingNominatimZoneTitle = q
            return
          }
        } catch (err) {
          const msg =
            err.data && err.data.message
              ? err.data.message
              : err.message || 'Nominatim'
          this.$toast.error(msg)
        }
      }

      this.finishWithViewportFallback(
        map,
        geometry,
        label,
        typesHint,
        placeRef,
      )
    },

    initAutoComplete(map, input) {
      if (!input) return
      const searchBox = new google.maps.places.SearchBox(input)

      map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds())
      })

      searchBox.addListener('places_changed', () => {
        const places = searchBox.getPlaces()
        if (!places.length) return

        const place = places[0]
        if (!place.geometry) {
          /* eslint-disable-next-line no-console */
          console.log('Returned place contains no geometry')
          return
        }

        this.clearPlaceSearchPreviewFeatures(map)

        const run = detail => {
          this.fetchNominatimBoundaryOrFallback(map, detail, place)
        }

        if (place.place_id) {
          const service = new google.maps.places.PlacesService(map)
          service.getDetails(
            {
              placeId: place.place_id,
              fields: ['geometry', 'name', 'formatted_address', 'types'],
            },
            (result, status) => {
              if (
                status === google.maps.places.PlacesServiceStatus.OK &&
                result &&
                result.geometry
              ) {
                run(result)
              } else {
                run(place)
              }
            },
          )
        } else {
          run(place)
        }
      })
    },

    /**
     * Single TOP_LEFT control: search, then pan / polygon (same height as #pac-input).
     */
    buildZoneMapTopToolbar(map) {
      const toolbar = document.createElement('div')
      toolbar.className = 'zone-map-top-toolbar'

      const pacInput = document.createElement('input')
      pacInput.id = 'pac-input'
      pacInput.className = 'controls'
      pacInput.type = 'text'
      pacInput.placeholder = this.$t('zones.type_to_search')
      toolbar.appendChild(pacInput)

      const panBtn = this.createToolbarDrawButton(
        '<i class="icon-hand"></i>',
        'pan',
        () => {
          map.data.setDrawingMode(null)
          this.setZoneDrawingToolbarMode(toolbar, 'pan')
        },
        this.$t('zones.map_mode_pan'),
      )
      const polyBtn = this.createToolbarDrawButton(
        '<i class="icon-vector"></i>',
        'polygon',
        () => {
          map.data.setDrawingMode('Polygon')
          this.setZoneDrawingToolbarMode(toolbar, 'polygon')
        },
        this.$t('zones.map_mode_draw_polygon'),
      )

      toolbar.appendChild(panBtn)
      toolbar.appendChild(polyBtn)

      map.data.setDrawingMode(null)
      this.setZoneDrawingToolbarMode(toolbar, 'pan')

      return { toolbar, pacInput }
    },

    createToolbarDrawButton(innerHtml, mode, clickHandler, hint) {
      const wrap = document.createElement('div')
      wrap.className = 'zone-map-toolbar-draw'
      wrap.dataset.drawMode = mode
      wrap.addEventListener('click', clickHandler)

      const face = document.createElement('div')
      face.className = 'zone-map-toolbar-draw__face'
      face.title = hint
      face.innerHTML = innerHtml
      wrap.appendChild(face)

      return wrap
    },

    setZoneDrawingToolbarMode(toolbar, mode) {
      toolbar.querySelectorAll('.zone-map-toolbar-draw').forEach(el => {
        el.classList.toggle(
          'zone-map-toolbar-draw--active',
          el.dataset.drawMode === mode,
        )
      })
    },

    createMapButton(innerHtml, clickHandler, hint) {
      // Create a div to hold the control.
      const controlDiv = document.createElement('div')
      controlDiv.addEventListener('click', clickHandler)

      // Set CSS for the control border
      const controlUI = document.createElement('div')
      controlUI.style.backgroundColor = '#fff'
      controlUI.style.boxShadow = 'rgba(0, 0, 0, 0.3) 0px 1px 4px -1px'
      controlUI.style.borderRadius = '2px'
      controlUI.style.marginRight = '10px'
      controlUI.style.border = '1px solid #fff'
      controlUI.style.cursor = 'pointer'
      controlUI.style.marginBottom = '10px'
      controlUI.style.textAlign = 'center'
      controlUI.title = hint
      controlDiv.appendChild(controlUI)

      // Set CSS for the control interior
      const controlText = document.createElement('div')
      controlText.style.color = 'rgb(25,25,25)'
      controlText.style.fontFamily = 'Roboto,Arial,sans-serif'
      controlText.style.fontSize = '16px'
      const hw = '38px'
      controlText.style.height = hw
      controlText.style.width = hw
      controlText.style.lineHeight = hw
      controlText.style.fill = '#666666'
      controlText.style.color = '#666666'
      controlText.innerHTML = innerHtml
      controlUI.appendChild(controlText)

      return controlDiv
    },

    addTabsControlButtonToMap() {
      const toggleMapManagerButton = this.createMapButton(
        '<i class="icon-list"></i>',
        this.toggleZoneManagerPanelVisibility,
        this.$t('zones.toggle_zone_manager'),
      )
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(
        toggleMapManagerButton,
      )
    },

    addSaveButton() {
      const saveDataLayerButton = this.createMapButton(
        '<i class="icon-floppy-disk"></i>',
        this.saveCurrentDataLayer,
        this.$t('zones.save'),
      )
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(
        saveDataLayerButton,
      )
    },

    addClearButton() {
      const clearButton = this.createMapButton(
        '<i class="icon-cross2"></i>',
        this.onClearMapButtonClick,
        this.$t('zones.clear_map_layers'),
      )
      this.map.controls[google.maps.ControlPosition.RIGHT_TOP].push(clearButton)
    },

    onClearMapButtonClick() {
      this.clearMapDataLayer()
      if (this.tab === TAB_COUNTRY_ZONE) this.unsetActiveCountryZone()
      else this.unsetActiveInCountryZone()
    },

    async saveZone(geoJson, options = {}) {
      const presetTitle =
        typeof options.presetTitle === 'string'
          ? options.presetTitle.trim()
          : ''

      const needUpdateInCountryZone =
        this.activeInCountryZone && this.tab === TAB_IN_COUNTRY_ZONE
      const needUpdateCountryZone =
        this.activeCountryZone && this.tab === TAB_COUNTRY_ZONE

      if (needUpdateInCountryZone || needUpdateCountryZone) {
        const zone = needUpdateCountryZone
          ? this.activeCountryZone
          : this.activeInCountryZone

        this.updateExistingZone({
          ...zone,
          geoJson,
        })
      } else {
        // add new zone
        if (this.tab === TAB_COUNTRY_ZONE) {
          let title = presetTitle
          let isNewMethod = false
          if (!title) {
            const result = await divisionPopup(
              { title: '', isNewMethod: false },
              this.$i18n,
            )

            if (result.dismiss) {
              return
            }

            title = result.value.name
            isNewMethod = result.value.isNewMethod
          }

          this.addCountryZone({
            title,
            isNewMethod,
            geoJson,
          }).catch(err => this.$toast.error(err.message))
        } else {
          let title = presetTitle
          if (!title) {
            const prompted = await this.prompt(this.$t('zones.new_zone'))
            title = prompted.value
          }
          if (title) {
            const zoneForSave = {
              title,
              geoJson,
            }
            this.addInCountryZone({
              ...zoneForSave,
              division_id: this.activeCountryZone.id,
            }).catch(err => this.$toast.error(err.data.message))
          }
        }
      }
    },

    saveCurrentDataLayer() {
      this.map.data.toGeoJson(async geoJson => {
        if (!(geoJson.features && geoJson.features.length)) return

        geoJson.features.forEach(f => {
          if (f.properties && f.properties.__placeSearchPreview)
            delete f.properties.__placeSearchPreview
        })

        await this.saveZone(geoJson)
      })
    },

    onCountryZoneRowClick(countryZone) {
      // clear old data layer
      this.clearMapDataLayer()

      // draw new data layer
      this.drawZoneOnMap(countryZone)
      this.fitMapToCurrentDataLayer()

      if (this.tab === TAB_COUNTRY_ZONE) {
        this.setActiveCountryZone(countryZone)
      } else if (this.tab === TAB_IN_COUNTRY_ZONE) {
        this.setActiveInCountryZone(countryZone)
      }
    },

    drawZoneOnMap(countryZone) {
      try {
        this.map.data.addGeoJson(countryZone.geoJson)
      } catch (err) {
        this.$toast.error(err.message)
      }
    },

    /**
     * clears all user drawing shapes/layers
     * from map's surface
     */
    clearMapDataLayer() {
      this.pendingNominatimZoneTitle = null
      if (!this.map) return
      const data = this.map.data
      data.forEach(feature => data.remove(feature))
    },

    async updateExistingZone(zoneForUpdate) {
      const { value: yes } = await Swal({
        type: 'question',
        title: `${this.$t('zones.want_to_update_zone')} ${
          zoneForUpdate.title
        }?`,
        showCancelButton: true,
      })

      if (yes) {
        if (this.tab === TAB_COUNTRY_ZONE)
          this.updateCountryZones(zoneForUpdate).catch(err =>
            this.$toast.error(err.data.message),
          )
        else if (this.tab === TAB_IN_COUNTRY_ZONE)
          this.updateInCountryZones({
            ...zoneForUpdate,
            division_id: this.activeCountryZone.id,
          }).catch(err => this.$toast.error(err.data.message))
      }
    },

    fitMapToCurrentDataLayer() {
      const bounds = new google.maps.LatLngBounds()
      this.map.data.forEach(function(feature) {
        feature.getGeometry().forEachLatLng(function(latlng) {
          bounds.extend(latlng)
        })
      })

      this.map.fitBounds(bounds)
    },

    onTabChanged(tabIndex) {
      this.tab = tabIndex
      switch (tabIndex) {
        case TAB_COUNTRY_ZONE:
          this.unsetActiveInCountryZone()
          if (this.activeCountryZone)
            this.onCountryZoneRowClick(this.activeCountryZone)
          break
        case TAB_IN_COUNTRY_ZONE:
          this.clearMapDataLayer()
          if (this.activeCountryZone?.id) {
            this.fetchInCountryZones(this.activeCountryZone.id).catch(err =>
              this.$toast.error(err.data.message),
            )
          }
          break
      }
    },
  },
}
</script>

<style scoped>
.zone-wrapper {
  display: flex;
  flex-direction: row;
  flex: 1;
  position: relative;
}

.zone-wrapper #map-holder {
  flex: 2;
}

.zone-nominatim-quick-save {
  position: fixed;
  z-index: 1050;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  width: min(560px, calc(100vw - 2rem));
  pointer-events: none;
}

.zone-nominatim-quick-save__bar {
  pointer-events: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  background: #1a73e8;
  color: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.18);
}

.zone-nominatim-quick-save__text {
  font-size: 14px;
  line-height: 1.35;
  flex: 1;
  min-width: 0;
}

.zone-nominatim-quick-save__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.zone-nominatim-quick-save__dismiss {
  //min-width: 2rem;
  //padding-left: 0.5rem;
  //padding-right: 0.5rem;
  //font-size: 1.25rem;
  //line-height: 1;
}

.zone-nominatim-banner-fade-enter-active,
.zone-nominatim-banner-fade-leave-active {
  transition: opacity 0.2s ease;
}

.zone-nominatim-banner-fade-enter,
.zone-nominatim-banner-fade-leave-to {
  opacity: 0;
}
</style>

<style>
/* Toolbar is moved under #map-holder by Google Maps (not under .zone-wrapper); keep unscoped. */
.zone-map-top-toolbar {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 10px;
  margin-top: 10px;
}

.zone-map-top-toolbar input#pac-input {
  margin-top: 0;
}

.zone-map-toolbar-draw {
  flex-shrink: 0;
  cursor: pointer;
}

.zone-map-toolbar-draw__face {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  background-color: #fff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 1px 4px -1px;
  border-radius: 2px;
  border: 1px solid #fff;
  color: #666;
  font-size: 16px;
}
.zone-map-toolbar-draw[data-draw-mode="polygon"] .zone-map-toolbar-draw__face {
  background-color: #f8f7f7;
  border-color: #e0dcdc;
}
</style>
